// This file contains the context and provider for managing case data in the application.
// It includes functionality for fetching, updating, and persisting case data,
// as well as handling real-time case updates via WebSocket connections.

// This component has access to following contexts:
// - api-context/AnimalTypeContext - the refactored version of the AnimalTypeContext
// - api-context/BreedsContext - the refactored version of the BreedsContext

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { gekkoApi, GekkoApiError } from '../../api/GekkoApi';
import { useAnimalTypes } from './AnimalTypeContext';
import { useBreeds } from './BreedsContext';
import { useSymptoms } from './SymptomsContext';
import {
  useWebSocket,
  type WebSocketMessage,
  type AttachmentUpdateMessage,
} from '../../hooks/useWebSocket';
import { SelectedTreatment } from '../../types/treatment.types';
import { useConfiguration } from '../ConfigurationContext';

// Interface for file information
interface FileInfo {
  id: string;
  name: string;
  size: number;
  type: string;
  extractedText?: string;
}

// Case status enum
export type CaseStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'INITIAL_AI_PROCESSING_DONE'
  | 'SANITIZING'
  | 'CALCULATING_DIAGNOSES'
  | 'COMPLETED'
  | 'ERROR'
  | 'QUEUED';

// Symptom interface
export interface Symptom {
  id: number;
  categoryName: string;
  symptomName: string;
  _distance?: number;
}

// Key phrases and symptom interface
export interface SymptomWithKeyPhrases {
  keyPhrases: string[];
  symptom: Symptom;
  textSnippets?: string[];
}

// Selected symptom interface
export interface SelectedSymptom {
  id: number;
  significant: boolean;
}

// Diagnosis result interface
export interface DiagnosisResult {
  id: number; // disease or animaltype disease? id
  name: string;
  probability: number;
  severity: number;
}

// Selected diagnosis interface (minimal, just ID for persistence)
export interface SelectedDiagnosis {
  id: number;
}

// Patient information interface
export interface AIExtractedPatient {
  animal_type_id: string;
  gender: string;
  date_of_birth: string;
  name: string;
  weight: number | null;
  neutered: boolean | null;
  breed: string;
}

// Patient interface for case data
export interface Patient {
  animal_type_id: number;
  breed_id: number;
  weight_kg: number;
  date_of_birth?: string;
  name?: string;
  gender_id?: number; // 1 = male, 2 = female
  neutered?: boolean;
  patient_id?: string;
}

// Attachment reference interface (attachment with status)
export interface CaseAttachmentRef {
  id: string;
  status: string;
}

// Case data interface
export interface CaseData {
  id?: string;
  notes: string;
  files?: FileInfo[];
  status?: CaseStatus;
  created_at?: string;
  updated_at?: string;

  // Enhanced fields
  selected_symptoms?: SelectedSymptom[];
  selected_diagnoses?: DiagnosisResult[];
  de_selected_symptoms?: number[];
  sanitized_symptoms?: SymptomWithKeyPhrases[];
  suggested_symptoms?: SymptomWithKeyPhrases[];
  attachments?: CaseAttachmentRef[];
  ai_extracted_patient?: AIExtractedPatient;
  patient?: Patient;
  diagnoses?: DiagnosisResult[];
  selected_treatments?: SelectedTreatment[];

  // Helper function to populate patient from ai_extracted_patient
  populatePatientFromAIExtracted?: (
    animalTypes: Array<{ id: number; name: string }>,
    breeds: Record<
      number,
      Array<{ id: number; name: string; animal_type_id: number }>
    >
  ) => void;
}

// Simplified case context interface
export interface CasesContextType {
  currentCase: CaseData | null;
  isDirty: boolean;
  setCaseProperty: <K extends keyof CaseData>(
    key: K,
    value: CaseData[K]
  ) => void;
  fetchCase: (caseId: string) => Promise<CaseData>;
  refreshCase: () => Promise<CaseData | null>;
  persistCase: () => Promise<void>;
  isLoading: boolean;
  isSaving: boolean;
  isRefreshing: boolean;
  webSocketStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  populatePatientFromAIExtracted: (
    caseData: CaseData,
    animalTypes: Array<{ id: number; name: string }>,
    breeds: Record<
      number,
      Array<{ id: number; name: string; animal_type_id: number }>
    >
  ) => Promise<CaseData>;
  // Add attachment update callback registration
  onAttachmentUpdate: (
    callback: (message: AttachmentUpdateMessage) => void
  ) => () => void;
  // Validation errors from API
  validationErrors: Record<string, string> | null;
  clearValidationErrors: () => void;
}

// Create the context with a default value
const CasesContext = createContext<CasesContextType | undefined>(undefined);

// Create the provider component
interface CasesProviderProps {
  children: ReactNode;
  case?: CaseData;
}

export const CasesProvider: React.FC<CasesProviderProps> = ({
  children,
  case: initialCase,
}) => {
  console.log('initing with', initialCase);

  // Backend case data (pristine from API)
  const [backendCase, setBackendCase] = useState<CaseData | null>(
    initialCase || null
  );

  // Local modifications tracking dirty properties
  const [localModifications, setLocalModifications] = useState<
    Partial<CaseData>
  >({});

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<Record<
    string,
    string
  > | null>(null);

  // Access animal types and breeds for auto-population
  const { animalTypes } = useAnimalTypes();
  const { breeds, fetchBreedsByAnimalTypeId } = useBreeds();
  const { ensureSymptoms } = useSymptoms();

  // Use refs to access current state without closure issues
  const currentCaseRef = useRef<CaseData | null>(null);
  const backendCaseRef = useRef<CaseData | null>(null);
  const localModificationsRef = useRef<Partial<CaseData>>({});

  // Attachment update callbacks
  const attachmentUpdateCallbacksRef = useRef<
    Set<(message: AttachmentUpdateMessage) => void>
  >(new Set());

  // Keep refs in sync with state
  useEffect(() => {
    backendCaseRef.current = backendCase;
  }, [backendCase]);

  useEffect(() => {
    localModificationsRef.current = localModifications;
  }, [localModifications]);

  // Computed currentCase that combines backend data with local modifications
  const currentCase = useMemo(() => {
    if (!backendCase) return null;
    const combined = { ...backendCase, ...localModifications };
    // Keep ref in sync
    currentCaseRef.current = combined;
    return combined;
  }, [backendCase, localModifications]);

  // Computed isDirty flag
  const isDirty = useMemo(() => {
    return Object.keys(localModifications).length > 0;
  }, [localModifications]);

  // Eager-load symptoms whenever we have a case with selected or suggested symptoms
  useEffect(() => {
    if (!currentCase) return;

    const symptomIds = new Set<number>();

    // Add selected symptom IDs
    currentCase.selected_symptoms?.forEach((s) => symptomIds.add(s.id));

    // Add suggested symptom IDs
    currentCase.suggested_symptoms?.forEach((item) =>
      symptomIds.add(item.symptom.id)
    );

    if (symptomIds.size > 0) {
      ensureSymptoms(Array.from(symptomIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCase?.selected_symptoms,
    currentCase?.suggested_symptoms,
    ensureSymptoms,
  ]);

  // Function to clear validation errors
  const clearValidationErrors = useCallback(() => {
    setValidationErrors(null);
  }, []);

  // Function to set individual case properties and track dirty state
  const setCaseProperty = useCallback(
    <K extends keyof CaseData>(key: K, value: CaseData[K]) => {
      // Clear validation errors when user modifies form
      setValidationErrors(null);

      setLocalModifications((prev) => {
        // If the value is the same as backend, remove from modifications
        if (backendCase && backendCase[key] === value) {
          const { [key]: _removed, ...rest } = prev;
          return rest;
        }

        // Otherwise, track the modification
        return { ...prev, [key]: value };
      });
    },
    [backendCase]
  );

  // Function to populate patient data from AI extracted data
  const populatePatientFromAIExtracted = useCallback(
    async (
      caseData: CaseData,
      animalTypes: Array<{ id: number; name: string }>,
      breeds: Record<
        number,
        Array<{ id: number; name: string; animal_type_id: number }>
      >
    ): Promise<CaseData> => {
      // If there's no AI extracted patient data or already has patient data, return as is
      if (!caseData.ai_extracted_patient || caseData.patient) {
        return caseData;
      }

      const aiPatient = caseData.ai_extracted_patient;
      const updatedCase = { ...caseData };

      // Try to match animal type ID
      const matchedAnimalType = animalTypes.find(
        (type) => type.id === Number(aiPatient.animal_type_id)
      );

      // Create a partial patient object with available info
      const newPatient: Partial<Patient> = {
        name: aiPatient.name,
        date_of_birth: aiPatient.date_of_birth,
        neutered: aiPatient.neutered || false,
        animal_type_id: matchedAnimalType ? matchedAnimalType.id : 0,
        weight_kg: aiPatient.weight || 0,
        gender_id: aiPatient.gender?.toLowerCase() === 'female' ? 2 : 1,
      };

      // Try to match breed if animal_type_id is available
      if (matchedAnimalType) {
        const animalTypeId = matchedAnimalType.id;

        // Fetch breeds for this animal type if not already available
        let breedList = breeds[animalTypeId];
        if (!breedList) {
          breedList = await fetchBreedsByAnimalTypeId(animalTypeId);
        }

        if (breedList && aiPatient.breed) {
          const aiBreedName = aiPatient.breed.toLowerCase();

          // Find a breed that starts with the AI extracted breed name
          const matchedBreed = breedList.find((breed) =>
            breed.name.toLowerCase().startsWith(aiBreedName)
          );

          if (matchedBreed) {
            newPatient.breed_id = matchedBreed.id;
          }
        }
      }

      updatedCase.patient = newPatient as Patient;
      return updatedCase;
    },
    [fetchBreedsByAnimalTypeId]
  );

  // Refresh the current case data from API
  const refreshCase = useCallback(async (): Promise<CaseData | null> => {
    if (!currentCase || !currentCase.id) {
      return null; // No case to refresh
    }

    setIsRefreshing(true);
    try {
      const response = await gekkoApi.get<CaseData>(`/cases/${currentCase.id}`);

      // Automatically populate patient data from AI extracted data if available and no patient already exists
      let processedResponse = response;
      if (response.ai_extracted_patient && !response.patient) {
        processedResponse = await populatePatientFromAIExtracted(
          response,
          animalTypes,
          breeds
        );
      }

      // Update backend case data
      setBackendCase(processedResponse);

      // Clean up local modifications that are now the same as backend
      setLocalModifications((prev) => {
        const cleanedModifications: Partial<CaseData> = {};

        for (const [key, value] of Object.entries(prev)) {
          const typedKey = key as keyof CaseData;
          if (processedResponse[typedKey] !== value) {
            cleanedModifications[typedKey] = value as any;
          }
        }

        return cleanedModifications;
      });

      return processedResponse;
    } catch (error) {
      console.error('Error refreshing case data:', error);
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [currentCase, populatePatientFromAIExtracted, animalTypes, breeds]);

  // Fetch case data from API
  const fetchCase = useCallback(
    async (caseId: string): Promise<CaseData> => {
      setIsLoading(true);
      try {
        const response = await gekkoApi.get<CaseData>(`/cases/${caseId}`);

        // Automatically populate patient data from AI extracted data if available and no patient already exists
        let processedResponse = response;
        console.log(
          'response',
          response.ai_extracted_patient,
          response.patient
        );
        if (response.ai_extracted_patient && !response.patient) {
          processedResponse = await populatePatientFromAIExtracted(
            response,
            animalTypes,
            breeds
          );
        }

        // Update backend case and reset dirty state
        setBackendCase(processedResponse);
        setLocalModifications({});

        return processedResponse;
      } catch (error) {
        console.error('Error fetching case data:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [populatePatientFromAIExtracted, animalTypes, breeds]
  );

  // Handle WebSocket messages for real-time case updates
  const handleWebSocketMessage = useCallback(
    async (message: WebSocketMessage) => {
      if (message.type === 'case_update') {
        const updatedCase = message.case;
        const currentCaseValue = currentCaseRef.current;

        // Only update if this is the current case we're viewing
        if (currentCaseValue && updatedCase.id === currentCaseValue.id) {
          console.log('Received real-time case update:', updatedCase);

          // Convert snake_case fields and ensure proper typing
          let processedCase: CaseData = {
            ...updatedCase,
            status: updatedCase.status as CaseStatus,
            created_at: updatedCase.created_at,
            updated_at: updatedCase.updated_at,
            ai_extracted_patient: updatedCase.ai_extracted_patient,
            selected_symptoms: updatedCase.selected_symptoms,
            de_selected_symptoms: updatedCase.de_selected_symptoms,
            sanitized_symptoms: updatedCase.sanitized_symptoms,
            suggested_symptoms: updatedCase.suggested_symptoms,
            attachments: updatedCase.attachments || [],
            notes: updatedCase.notes,
          };

          // Automatically populate patient data from AI extracted data if available and no patient already exists
          if (processedCase.ai_extracted_patient && !processedCase.patient) {
            processedCase = await populatePatientFromAIExtracted(
              processedCase,
              animalTypes,
              breeds
            );
          }

          // Update backend case data
          setBackendCase(processedCase);

          // Clean up local modifications that are now the same as backend
          setLocalModifications((prev) => {
            const cleanedModifications: Partial<CaseData> = {};

            for (const [key, value] of Object.entries(prev)) {
              const typedKey = key as keyof CaseData;
              if (processedCase[typedKey] !== value) {
                cleanedModifications[typedKey] = value as any;
              }
            }

            return cleanedModifications;
          });
        }
      } else if (message.type === 'attachment_update') {
        // Forward attachment updates to registered callbacks
        attachmentUpdateCallbacksRef.current.forEach((callback) => {
          callback(message);
        });
      }
    },
    [populatePatientFromAIExtracted, animalTypes, breeds] // Added dependencies for populating patient
  );

  // Determine if we should use polling instead of WebSocket
  const { config } = useConfiguration();
  const usePolling = !!config.pollingIntervalMs;

  // Setup WebSocket connection (only when not polling)
  const { connectionStatus, connect, disconnect } = useWebSocket(
    handleWebSocketMessage
  );

  const currentCaseId = useMemo(() => currentCase?.id, [currentCase]);
  console.log('currentCaseId', currentCaseId);

  // Setup WebSocket connection when we have a case (skip if polling)
  useEffect(() => {
    if (usePolling) return;

    if (currentCaseId) {
      console.log('Connecting to WebSocket for case updates');
      connect();
    } else {
      console.log('Disconnecting from WebSocket - no active case');
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [currentCaseId, connect, disconnect, usePolling]);

  // Silent polling as alternative to WebSocket
  useEffect(() => {
    if (!usePolling || !currentCaseId) return;

    const poll = async () => {
      try {
        const response = await gekkoApi.get<CaseData>(
          `/cases/${currentCaseId}`
        );

        let processedResponse = response;
        if (response.ai_extracted_patient && !response.patient) {
          processedResponse = await populatePatientFromAIExtracted(
            response,
            animalTypes,
            breeds
          );
        }

        setBackendCase(processedResponse);

        setLocalModifications((prev) => {
          const cleaned: Partial<CaseData> = {};
          for (const [key, value] of Object.entries(prev)) {
            const typedKey = key as keyof CaseData;
            if (processedResponse[typedKey] !== value) {
              cleaned[typedKey] = value as any;
            }
          }
          return cleaned;
        });
      } catch (error) {
        // Silent failure - don't disrupt the user
      }
    };

    const intervalId = setInterval(poll, config.pollingIntervalMs!);
    return () => clearInterval(intervalId);
  }, [
    usePolling,
    currentCaseId,
    config.pollingIntervalMs,
    populatePatientFromAIExtracted,
    animalTypes,
    breeds,
  ]);

  // Persist current case to API
  const persistCase = useCallback(async (): Promise<void> => {
    setIsSaving(true);
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        // Access current state via refs to avoid stale closure
        const currentBackendCase = backendCaseRef.current;
        const currentLocalMods = localModificationsRef.current;

        // Check if we have data to save
        if (
          !currentBackendCase?.id ||
          Object.keys(currentLocalMods).length === 0
        ) {
          return; // Nothing to save or not dirty
        }

        // Combine backend case with local modifications
        const combinedCase = { ...currentBackendCase, ...currentLocalMods };

        // Send the combined case data (backend + local modifications), excluding status
        const { status: _status, ...caseDataToSend } = combinedCase;

        // Check if patient data is clearly missing all required fields
        // If so, only send notes and exclude incomplete patient data
        const patient = caseDataToSend.patient;
        const isMissingAllRequiredPatientInfo =
          patient &&
          !patient.animal_type_id &&
          !patient.breed_id &&
          !patient.weight_kg;

        if (isMissingAllRequiredPatientInfo) {
          // Remove incomplete patient data - only send notes
          delete caseDataToSend.patient;
        } else if (patient && patient.neutered == null) {
          // Ensure patient.neutered is a boolean (API requires boolean, not null)
          caseDataToSend.patient = {
            ...patient,
            neutered: false,
          };
        }
        gekkoApi
          .put<CaseData>(`/cases/${combinedCase.id}`, {
            body: caseDataToSend,
          })
          .then(() => {
            // After successful save, merge local modifications into backend case
            setBackendCase(combinedCase);
            setLocalModifications({});
            setValidationErrors(null);
            resolve();
          })
          .catch((error) => {
            console.error('Error persisting case data:', error);

            // Extract validation errors from API response
            if (
              error instanceof GekkoApiError &&
              error.body?.errors &&
              typeof error.body.errors === 'object'
            ) {
              setValidationErrors(error.body.errors as Record<string, string>);
            }

            reject(error);
          })
          .finally(() => {
            setIsSaving(false);
          });
      });
    });
  }, []); // Empty dependency array - reads state via refs

  // Attachment update callback registration
  const onAttachmentUpdate = useCallback(
    (callback: (message: AttachmentUpdateMessage) => void) => {
      attachmentUpdateCallbacksRef.current.add(callback);

      // Return cleanup function
      return () => {
        attachmentUpdateCallbacksRef.current.delete(callback);
      };
    },
    []
  );

  const value = {
    currentCase,
    isDirty,
    setCaseProperty,
    fetchCase,
    refreshCase,
    persistCase,
    isLoading,
    isSaving,
    isRefreshing,
    webSocketStatus: usePolling ? 'connected' : connectionStatus,
    populatePatientFromAIExtracted,
    onAttachmentUpdate,
    validationErrors,
    clearValidationErrors,
  };

  return (
    <CasesContext.Provider value={value}>{children}</CasesContext.Provider>
  );
};

// Custom hook to use the cases context
export const useCases = () => {
  const context = useContext(CasesContext);
  if (context === undefined) {
    throw new Error('useCases must be used within a CasesProvider');
  }
  return context;
};
